import { OnChangeFn, SortingState, Updater } from '@tanstack/react-table';

import { TableProps } from '@/components/table/types';

interface UseTableSortingStateResult {
  /**
   * Controlled `SortingState` for `useReactTable({ state: { sorting } })`.
   * Single-entry (server sort is single-column) or empty when no `sort` prop.
   */
  sortingState: SortingState;
  /** `OnChangeFn<SortingState>` for `useReactTable({ onSortingChange })`. */
  onSortingChange: OnChangeFn<SortingState>;
}

/**
 * Bridge `TableProps.sort` ({ orderBy, descending, onSort }) ↔ react-table
 * `SortingState`, for CONTROLLED, SERVER-SIDE sort.
 *
 * The consumer wires this into `useReactTable` with `manualSorting: true` and
 * NO `getSortedRowModel` — react-table must not re-sort rows on the client; the
 * server returns rows already ordered and `onSort` triggers the refetch. The
 * sort direction is owned by the parent (`sort.orderBy`/`sort.descending`); this
 * hook only projects that into `SortingState` and translates react-table's
 * header-click intent back into a `sort.onSort(...)` call.
 *
 * Toggle semantics match `Table.tsx`'s header click handler verbatim
 * (`Table.tsx:768-785`): clicking the CURRENT sort column flips `descending`;
 * clicking a NEW column defaults to `descending: true`. react-table's own
 * desc→asc→none cycle is intentionally NOT used — the binary toggle rule lives
 * here so behavior is identical to `Table.tsx`.
 *
 * @param sort - The consumer's `TableProps.sort`; `undefined` when the table is
 *   not sortable (then `sortingState` is empty and `onSortingChange` no-ops).
 */
export function useTableSortingState<TData>(
  sort: TableProps<TData>['sort']
): UseTableSortingStateResult {
  // Project the controlled prop into react-table's shape. react-table ids are
  // strings; `sort.orderBy` is `keyof TData`, so stringify for the id.
  const sortingState: SortingState = sort
    ? [{ id: String(sort.orderBy), desc: sort.descending }]
    : [];

  const onSortingChange: OnChangeFn<SortingState> = (
    updater: Updater<SortingState>
  ) => {
    if (!sort) {
      return;
    }

    // Resolve react-table's updater against the current state to learn which
    // column the user is acting on (the click target).
    const next =
      typeof updater === 'function' ? updater(sortingState) : updater;

    // Clearing sort has no controlled path (Table.tsx always sorts on click);
    // ignore an empty target so we never drop the server sort.
    const target = next[0];
    if (!target) {
      return;
    }

    // Binary toggle rule, identical to Table.tsx:768-785.
    const isCurrentColumn = target.id === String(sort.orderBy);
    sort.onSort({
      orderBy: target.id as keyof TData,
      descending: isCurrentColumn ? !sort.descending : true,
    });
  };

  return { sortingState, onSortingChange };
}
